// Вход, регистрация, «забыли пароль» и восстановление удалённого аккаунта —
// четыре состояния одного окна.
//
// Вход и регистрация переключаются вкладками: это два равноправных входа в
// продукт, и прятать один из них в ссылку под формой значит делать вид, что
// выбор уже сделан. Восстановление пароля и восстановление аккаунта вкладками
// не являются — это ответвления (от входа и от регистрации соответственно),
// поэтому там вкладки уступают место кнопке «назад».
import {
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { Logo, Modal } from '../ui/shell';
import { useI18n, useT } from '../i18n';
import { useOnboarding } from '../onboarding';
import { useAuth } from './AuthProvider';
import { forgotPassword, restoreAccount } from './api';
import { errorKey, passwordProblemKeys } from './errors';
import type { ApiError } from './api';
import styles from './AuthModal.module.css';

export type AuthMode = 'signin' | 'signup';
// 'restore' — не вкладка, а ответвление от регистрации: сервер отвечает
// 'restorable', когда почта принадлежит удалённому аккаунту (см.
// server/auth/routes.mjs), и вместо ошибки нужно предложить вернуть его
// старым паролем, а не завести новый.
type Screen = AuthMode | 'forgot' | 'restore';

/** Минимальная длина пароля — та же, что проверяет сервер (server/auth/password.mjs). */
const PASSWORD_MIN = 10;

const TABS: AuthMode[] = ['signin', 'signup'];

export interface AuthModalProps {
  mode?: AuthMode;
  onClose(): void;
}

function EyeIcon({ off }: { off: boolean }): ReactElement {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3.2" />
      {off && <path d="M4 20 20 4" />}
    </svg>
  );
}

function AlertIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={styles.alertIcon}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5M12 16.4v.2" />
    </svg>
  );
}

export function AuthModal({ mode = 'signin', onClose }: AuthModalProps): ReactElement {
  const t = useT();
  const { lang } = useI18n();
  const { signIn, signUp, applyUser } = useAuth();
  const onboarding = useOnboarding();
  const ids = useId();
  const tabsRef = useRef<HTMLDivElement>(null);

  const [screen, setScreen] = useState<Screen>(mode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [sent, setSent] = useState(false);

  function switchTo(next: Screen): void {
    setScreen(next);
    setError(null);
    setSent(false);
    setPassword('');
    setRevealed(false);
  }

  /** Стрелки внутри вкладок — обязательная часть роли tablist. */
  function onTabKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const next = TABS[(TABS.indexOf(screen as AuthMode) + 1) % TABS.length];
    switchTo(next);
    // Фокус едет за выбранной вкладкой, иначе стрелки «теряют» пользователя.
    requestAnimationFrame(() => {
      tabsRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus();
    });
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);

    if (screen === 'forgot') {
      await forgotPassword({ email: email.trim(), lang });
      // Ответ намеренно одинаков для существующей и несуществующей почты.
      setSent(true);
      setBusy(false);
      return;
    }

    if (screen === 'restore') {
      const res = await restoreAccount(email.trim(), password);
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      // Сервер уже выдал сессию — applyUser переводит AuthProvider в
      // состояние 'user' без лишнего round-trip на /api/auth/me.
      applyUser(res.value.user);
      onClose();
      return;
    }

    const res =
      screen === 'signin'
        ? await signIn({ email: email.trim(), password })
        : await signUp({ email: email.trim(), password, name: name.trim(), lang });

    setBusy(false);
    if (!res.ok) {
      // Сервер отличает «почта занята» от «почта принадлежит удалённому
      // аккаунту»: во втором случае вместо ошибки предлагаем восстановление —
      // тем же паролем, что был до удаления.
      if (screen === 'signup' && res.error.error === 'restorable') {
        switchTo('restore');
        return;
      }
      setError(res.error);
      return;
    }

    onClose();
    // Тур показывается только после регистрации: вошедший второй раз уже видел
    // его и звать заново незачем — для этого есть компас в полосе поездки.
    if (screen === 'signup') onboarding.start();
  }

  const problems = passwordProblemKeys(error?.problems);
  const longEnough = password.length >= PASSWORD_MIN;
  // 'forgot' и 'restore' — оба ответвления без вкладок, у каждого своя пара
  // заголовок/кнопка и своя надпись вместо auth.subtitle.
  const title =
    screen === 'signin'
      ? t('auth.signin')
      : screen === 'signup'
        ? t('auth.signup')
        : screen === 'restore'
          ? t('auth.restore.title')
          : t('auth.forgot.title');

  const submitLabel =
    screen === 'signin'
      ? t('auth.signin')
      : screen === 'signup'
        ? t('auth.submit.signup')
        : screen === 'restore'
          ? t('auth.restore.submit')
          : t('auth.forgot.submit');

  const noTabs = screen === 'forgot' || screen === 'restore';

  return (
    <Modal title={title} onClose={onClose} bare>
      <div className={styles.head}>
        <Logo className={styles.logo} title={t('app.brand')} />
        <p className={styles.subtitle}>
          {t(
            screen === 'forgot'
              ? 'auth.forgot.hint'
              : screen === 'restore'
                ? 'auth.restore.hint'
                : 'auth.subtitle',
          )}
        </p>
      </div>

      {noTabs ? (
        <div className={styles.backRow}>
          <button
            type="button"
            className={styles.back}
            onClick={() => switchTo(screen === 'forgot' ? 'signin' : 'signup')}
          >
            ← {t(screen === 'forgot' ? 'auth.forgot.back' : 'auth.restore.back')}
          </button>
        </div>
      ) : (
        <div
          className={styles.tabs}
          role="tablist"
          aria-label={t('auth.tabs.label')}
          ref={tabsRef}
          onKeyDown={onTabKeyDown}
        >
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`${ids}-tab-${tab}`}
              aria-selected={screen === tab}
              aria-controls={`${ids}-panel`}
              tabIndex={screen === tab ? 0 : -1}
              className={`${styles.tab} ${screen === tab ? styles.tabActive : ''}`}
              onClick={() => switchTo(tab)}
            >
              {t(tab === 'signin' ? 'auth.signin' : 'auth.signup')}
            </button>
          ))}
        </div>
      )}

      <form
        className={styles.form}
        onSubmit={handleSubmit}
        id={`${ids}-panel`}
        role={noTabs ? undefined : 'tabpanel'}
        aria-labelledby={noTabs ? undefined : `${ids}-tab-${screen}`}
      >
        {sent ? (
          <p className={styles.notice}>{t('auth.forgot.sent')}</p>
        ) : (
          <>
            {error && (
              <div className={styles.error} role="alert">
                <AlertIcon />
                <div>
                  {t(errorKey(error))}
                  {problems.length > 0 && (
                    <ul className={styles.errorList}>
                      {problems.map((key) => (
                        <li key={key}>{t(key)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {screen === 'signup' && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor={`${ids}-name`}>
                  {t('auth.name')}
                </label>
                <input
                  id={`${ids}-name`}
                  className={styles.control}
                  type="text"
                  value={name}
                  maxLength={80}
                  autoComplete="name"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label} htmlFor={`${ids}-email`}>
                {t('auth.email')}
              </label>
              <input
                id={`${ids}-email`}
                className={styles.control}
                type="email"
                value={email}
                required
                autoFocus
                autoComplete="email"
                placeholder="name@example.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {screen !== 'forgot' && (
              <div className={styles.field}>
                <div className={styles.labelRow}>
                  <label className={styles.label} htmlFor={`${ids}-password`}>
                    {t('auth.password')}
                  </label>
                  {screen === 'signin' && (
                    <button
                      type="button"
                      className={styles.inlineLink}
                      onClick={() => switchTo('forgot')}
                    >
                      {t('auth.forgot.link')}
                    </button>
                  )}
                </div>

                <div className={styles.inputWrap}>
                  <input
                    id={`${ids}-password`}
                    className={`${styles.control} ${styles.controlWithButton}`}
                    type={revealed ? 'text' : 'password'}
                    value={password}
                    required
                    autoComplete={screen === 'signup' ? 'new-password' : 'current-password'}
                    aria-describedby={screen === 'signup' ? `${ids}-pwhint` : undefined}
                    onChange={(e) => setPassword(e.target.value)}
                    // Caps Lock — самая частая причина «пароль же верный».
                    onKeyUp={(e) => setCapsLock(e.getModifierState?.('CapsLock') ?? false)}
                    onBlur={() => setCapsLock(false)}
                  />
                  <button
                    type="button"
                    className={styles.reveal}
                    aria-label={t(revealed ? 'auth.password.hide' : 'auth.password.show')}
                    aria-pressed={revealed}
                    onClick={() => setRevealed((v) => !v)}
                  >
                    <EyeIcon off={revealed} />
                  </button>
                </div>

                {capsLock && <p className={styles.capsHint}>{t('auth.capslock')}</p>}

                {screen === 'signup' && (
                  <p
                    id={`${ids}-pwhint`}
                    className={`${styles.hint} ${longEnough ? styles.hintOk : ''}`}
                  >
                    {longEnough ? '✓ ' : ''}
                    {t('auth.pw.rule')}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {!sent && (
          <button type="submit" className={styles.submit} disabled={busy}>
            {busy && <span className={styles.spinner} aria-hidden="true" />}
            {busy ? t('auth.busy') : submitLabel}
          </button>
        )}
      </form>

      {(screen === 'signin' || screen === 'signup') && (
        <p className={styles.foot}>{t('auth.guest.hint')}</p>
      )}
    </Modal>
  );
}
